import { useEffect, useState } from 'react';
import { mindtimeWazuhComplianceApi } from 'src/api/mindtime/wazuhComplianceApi';
import { useUserProfile } from 'src/contexts/UserProfileContext';
import { useEdrDataGate } from 'src/mindtime/hooks/useEdrDataGate';
import { useWazuhScope } from 'src/mindtime/hooks/useWazuhScope';
import logger from 'src/utils/logger';
import type { SecurityToolbarFilterOption } from '../shared/securityDashboardToolbar';
import { COMPLIANCE_FRAMEWORK_FILTER_OPTIONS } from './complianceFrameworkOptions';

/** GET /api/v1/compliance/frameworks — reseller omits org until selected; client always uses profile org. */
export const useComplianceFrameworkFilterOptions = (
  pageOrganizationId?: string
): SecurityToolbarFilterOption[] => {
  const { isReseller } = useWazuhScope();
  const { userData } = useUserProfile();
  const { wait, isPreviewMode } = useEdrDataGate('configuration-compliance');
  const profileOrgId = userData?.organization?.id;
  const [options, setOptions] = useState<SecurityToolbarFilterOption[]>([]);
  const organizationId = isReseller ? pageOrganizationId?.trim() || undefined : profileOrgId;

  useEffect(() => {
    if (wait) return undefined;

    if (isPreviewMode) {
      setOptions(COMPLIANCE_FRAMEWORK_FILTER_OPTIONS);
      return undefined;
    }

    let cancelled = false;

    if (!isReseller && !organizationId) {
      setOptions([]);
      return undefined;
    }

    mindtimeWazuhComplianceApi
      .getFrameworks(organizationId ? { organizationId } : undefined)
      .then((frameworks) => {
        if (cancelled) return;
        setOptions(frameworks.map((name) => ({ label: name, value: name })));
      })
      .catch((error) => {
        logger(error, 'error', 'useComplianceFrameworkFilterOptions');
        if (!cancelled) setOptions([]);
      });

    return () => {
      cancelled = true;
    };
  }, [isReseller, organizationId, isPreviewMode, wait]);

  return options;
};
